import { Suspense, useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ProductProvider } from "@/context/ProductContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AppLayout } from "@/components/AppLayout";
import Landing from "@/pages/Landing";
import Home from "@/pages/Home";
import HowItWorks from "@/pages/HowItWorks";
import WhatRemyCanDo from "@/pages/WhatRemyCanDo";
import Contact from "@/pages/Contact";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import SmsOptIn from "@/pages/SmsOptIn";
import Onboarding from "@/pages/Onboarding";
import Simulator from "@/pages/Simulator";
import WebChat from "@/pages/WebChat";
import Settings from "@/pages/Settings";
import ActivityPage from "@/pages/Activity";
import Memory from "@/pages/Memory";
import Tasks from "@/pages/Tasks";
import Review from "@/pages/Review";
import Admin from "@/pages/Admin";
import { Terms, Privacy } from "@/pages/LegalPages";
import "@/App.css";

const wrap = (node: JSX.Element) => (
  <ProtectedRoute>
    <AppLayout>{node}</AppLayout>
  </ProtectedRoute>
);

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

export default function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
      <ProductProvider>
        <AuthProvider>
          <BrowserRouter>
            <Suspense fallback={null}>
              <ScrollToTop />
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/how-it-works" element={<HowItWorks />} />
                <Route path="/what-remy-can-do" element={<WhatRemyCanDo />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/sms-opt-in" element={<SmsOptIn />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route
                  path="/onboarding"
                  element={
                    <ProtectedRoute>
                      <Onboarding />
                    </ProtectedRoute>
                  }
                />
                <Route path="/chat" element={wrap(<WebChat />)} />
                <Route path="/simulator" element={wrap(<Simulator />)} />
                <Route path="/activity" element={wrap(<ActivityPage />)} />
                <Route path="/memory" element={wrap(<Memory />)} />
                <Route path="/tasks" element={wrap(<Tasks />)} />
                <Route path="/review/:token" element={wrap(<Review />)} />
                <Route path="/settings" element={wrap(<Settings />)} />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute adminOnly>
                      <AppLayout>
                        <Admin />
                      </AppLayout>
                    </ProtectedRoute>
                  }
                />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </ProductProvider>
    </ThemeProvider>
  );
}
